import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PlusIcon, ArrowSquareOutIcon } from "@phosphor-icons/react";

import Layout from "../../components/Layout";
import EntityHeader from "../../components/crm/EntityHeader";
import {
  ErrorBanner,
  INPUT_CLS,
  PrimaryButton,
} from "../../components/crm/Form";
import TeamRoleBadge from "../../components/team/TeamRoleBadge";
import AddTeamMemberModal from "../../components/team/AddTeamMemberModal";
import TempPasswordModal from "../../components/team/TempPasswordModal";
import { useAuth } from "../../contexts/AuthContext";
import { formatApiError } from "../../lib/api";
import {
  getInitials,
  listTeam,
  ROLE_OPTIONS,
} from "../../lib/team";

const ROLE_FILTERS = [
  { value: "", label: "All" },
  ...ROLE_OPTIONS,
];

export default function TeamList() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const isOffice = isOwner || user?.role === "ops";

  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();

  const role = params.get("role") || "";
  const isActiveParam = params.get("is_active") || "";
  const search = params.get("search") || "";

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [searchInput, setSearchInput] = useState(search);
  const [createOpen, setCreateOpen] = useState(false);
  const [tempPw, setTempPw] = useState(null); // {member, password} after creation

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const queryParams = {};
      if (role) queryParams.role = role;
      if (search) queryParams.search = search;
      // Office roles can flip the filter; techs ignored server-side.
      if (isOffice && isActiveParam !== "") {
        queryParams.is_active = isActiveParam === "true";
      } else if (isOffice && isActiveParam === "") {
        // Default: show only active for owner/ops UI unless they toggle.
        queryParams.is_active = true;
      }
      const data = await listTeam(queryParams);
      setItems(data || []);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [role, isActiveParam, search, isOffice]);

  useEffect(() => {
    load();
  }, [load]);

  // Sync search input when URL changes externally
  useEffect(() => {
    setSearchInput(search);
  }, [search]);

  // Debounce push of searchInput → URL
  useEffect(() => {
    const handle = setTimeout(() => {
      if (searchInput === search) return;
      const next = new URLSearchParams(params);
      if (searchInput) next.set("search", searchInput);
      else next.delete("search");
      setParams(next);
    }, 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const setFilter = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    setParams(next);
  };

  return (
    <Layout>
      <div className="space-y-6">
        <EntityHeader
          title="Team"
          subtitle="Manage operators, technicians, and access"
          actions={
            isOwner && (
              <PrimaryButton
                onClick={() => setCreateOpen(true)}
                data-testid="team-list-add-btn"
              >
                <PlusIcon size={16} className="mr-1.5" /> Add team member
              </PrimaryButton>
            )
          }
        />
        <ErrorBanner message={err} />

        <div className="bg-white border border-slate-200 rounded-md p-4 space-y-3">
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by name or email…"
            data-testid="team-list-search"
            className={INPUT_CLS}
          />
          <FilterChipRow
            label="Role"
            options={ROLE_FILTERS}
            value={role}
            onChange={(v) => setFilter("role", v)}
            testidPrefix="team-role-filter"
          />
          {isOffice && (
            <FilterChipRow
              label="Status"
              options={[
                { value: "", label: "Active" },
                { value: "true", label: "Active only" },
                { value: "false", label: "Inactive only" },
              ]}
              value={isActiveParam}
              onChange={(v) => setFilter("is_active", v)}
              testidPrefix="team-status-filter"
            />
          )}
          <div className="text-[11px] text-slate-500 pt-1">
            Showing {items.length}
          </div>
        </div>

        <div
          className="bg-white border border-slate-200 rounded-md overflow-hidden"
          data-testid="team-list"
        >
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-left text-[10px] uppercase tracking-wider text-slate-500 font-bold">
                <th className="px-4 py-2.5"></th>
                <th className="px-4 py-2.5">Name</th>
                <th className="px-4 py-2.5">Role</th>
                <th className="px-4 py-2.5">Phone</th>
                <th className="px-4 py-2.5">Regions</th>
                <th className="px-4 py-2.5">Status</th>
                <th className="px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {loading && items.length === 0 && <SkeletonRows count={5} cols={7} />}
              {!loading && items.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    className="px-4 py-10 text-center text-slate-500 text-sm"
                  >
                    No team members match the current filter.
                  </td>
                </tr>
              )}
              {items.map((m) => (
                <tr
                  key={m.id}
                  data-testid={`team-row-${m.id}`}
                  onClick={() => navigate(`/team/${m.id}`)}
                  className={`border-t border-slate-200 hover:bg-slate-50/70 cursor-pointer ${
                    m.is_active ? "" : "opacity-60"
                  }`}
                >
                  <td className="px-4 py-2.5 align-middle">
                    <div
                      className="h-8 w-8 rounded-full bg-slate-200 text-slate-700 flex items-center justify-center text-xs font-bold"
                      aria-hidden="true"
                    >
                      {getInitials(m.name)}
                    </div>
                  </td>
                  <td className="px-4 py-2.5 align-middle">
                    <div className="text-sm font-semibold text-slate-900">
                      {m.name}
                    </div>
                    <div className="text-xs text-slate-500">{m.email}</div>
                  </td>
                  <td className="px-4 py-2.5 align-middle">
                    <TeamRoleBadge role={m.role} />
                  </td>
                  <td className="px-4 py-2.5 align-middle text-xs text-slate-700 font-data">
                    {m.phone || "—"}
                  </td>
                  <td className="px-4 py-2.5 align-middle text-xs text-slate-700">
                    {m.service_regions && m.service_regions.length > 0
                      ? m.service_regions.slice(0, 2).join(", ") +
                        (m.service_regions.length > 2
                          ? ` +${m.service_regions.length - 2}`
                          : "")
                      : "—"}
                  </td>
                  <td className="px-4 py-2.5 align-middle">
                    {m.is_active ? (
                      <span
                        data-testid={`team-status-active-${m.id}`}
                        className="inline-flex items-center rounded-md border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-[10px] font-bold uppercase text-emerald-800"
                      >
                        Active
                      </span>
                    ) : (
                      <span
                        data-testid={`team-status-inactive-${m.id}`}
                        className="inline-flex items-center rounded-md border border-slate-300 bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase text-slate-500"
                      >
                        Inactive
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 align-middle text-right">
                    <span
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/team/${m.id}`);
                      }}
                      data-testid={`team-row-open-${m.id}`}
                      className="rounded-md border border-slate-200 hover:border-slate-400 p-1.5 transition inline-flex"
                    >
                      <ArrowSquareOutIcon size={14} className="text-slate-700" />
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <AddTeamMemberModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(created) => {
          setCreateOpen(false);
          setTempPw({ member: created, password: created.temp_password });
          load();
        }}
      />

      <TempPasswordModal
        open={Boolean(tempPw)}
        onClose={() => {
          const created = tempPw?.member;
          setTempPw(null);
          if (created) navigate(`/team/${created.id}`);
        }}
        email={tempPw?.member?.email}
        memberName={tempPw?.member?.name}
        tempPassword={tempPw?.password}
        variant="create"
      />
    </Layout>
  );
}

function FilterChipRow({ label, options, value, onChange, testidPrefix }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mr-1 w-16">
        {label}
      </span>
      {options.map((o) => {
        const isActive = value === o.value || (!value && o.value === "");
        return (
          <button
            key={o.value || "_all"}
            type="button"
            data-testid={`${testidPrefix}-${o.value || "all"}`}
            onClick={() => onChange(o.value)}
            className={`text-xs px-2.5 py-1 rounded-md border transition ${
              isActive
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-700 border-slate-300 hover:border-slate-400"
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function SkeletonRows({ count = 5, cols = 7 }) {
  return Array.from({ length: count }).map((_, i) => (
    <tr key={`sk-${i}`} className="border-t border-slate-200">
      {Array.from({ length: cols }).map((__, j) => (
        <td key={j} className="px-4 py-3">
          <div className="h-3 w-full max-w-[140px] rounded bg-slate-100 animate-pulse" />
        </td>
      ))}
    </tr>
  ));
}
